import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import TribeLogo, { TribeMark } from "@/components/TribeLogo";
import {
  LayoutDashboard, Users, Building2, ArrowLeftRight, Gift, Bell, LogOut, Menu, ChevronRight, FileText, UserCheck, Settings, Zap, ScanLine
} from "lucide-react";

const adminNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview" },
  { to: "/vendors", icon: Building2, label: "Vendors" },
  { to: "/customers", icon: Users, label: "Customers" },
  { to: "/transactions", icon: ArrowLeftRight, label: "Transactions" },
  { to: "/rewards", icon: Gift, label: "Rewards" },
  { to: "/nudge", icon: Bell, label: "Nudge Center" },
  { to: "/approvals", icon: UserCheck, label: "Approvals" },
  { to: "/settings", icon: Settings, label: "Settings" },
  { to: "/help", icon: FileText, label: "API Docs" },
];

const vendorNavItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview" },
  { to: "/my-customers", icon: Users, label: "Customers" },
  { to: "/design", icon: Building2, label: "Design" },
  { to: "/my-rewards", icon: Gift, label: "Rewards" },
  { to: "/earning-rules", icon: Zap, label: "Earning Rules" },
  { to: "/nudge", icon: Bell, label: "Nudge" },
  { to: "/scan", icon: ScanLine, label: "Till Scan" },
  { to: "/my-settings", icon: Settings, label: "Settings" },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = isAdmin ? adminNavItems : vendorNavItems;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-white">
      <div className="px-6 py-5 border-b border-line">
        <div className="flex items-center gap-2.5">
          <TribeMark size={32} />
          <TribeLogo size="sm" />
        </div>
        <p className="text-quiet text-xs mt-1.5 font-body">{isAdmin ? "Superadmin Console" : "Vendor Dashboard"}</p>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-pill text-sm font-medium font-body transition-all duration-150 ${
                isActive
                  ? "bg-purple-50 text-purple-500"
                  : "text-quiet hover:bg-purple-50 hover:text-ink"
              }`
            }
            data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
          >
            <Icon size={16} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-line">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 text-xs font-bold font-display">
            {user?.name?.[0]?.toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-ink text-xs font-medium truncate">{user?.name || "Admin"}</p>
            <p className="text-quiet text-xs truncate">{user?.email || ""}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-3 w-full px-3 py-2.5 text-quiet hover:text-danger hover:bg-purple-50 rounded-pill text-sm transition-all duration-150"
        >
          <LogOut size={16} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-surface font-body overflow-hidden">
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-line flex-shrink-0">
        <SidebarContent />
      </aside>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-ink/60" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-white border-r border-line z-10">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-line px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0">
          <button
            className="lg:hidden p-1.5 rounded-pill hover:bg-purple-50 text-quiet min-h-11 min-w-11 flex items-center justify-center"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu size={20} />
          </button>
          <div className="hidden lg:flex items-center gap-2 text-xs text-quiet">
            <ChevronRight size={12} />
            <span className="font-display font-bold text-ink text-xl leading-6">{isAdmin ? "Superadmin Dashboard" : "Vendor Dashboard"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-pill bg-purple-50 text-purple-700 text-xs font-bold font-body">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-500 inline-block" />
              {isAdmin ? "Admin" : "Vendor"}
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-5">
          {children}
        </main>
      </div>
    </div>
  );
}
